import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SearchableSelect from '../SearchableSelect';
import EditableDropdown from '../EditableDropdown';
import { EXCHANGE_OPTIONS, CURRENCY_OPTIONS, COUNTRY_OPTIONS } from './investmentConstants';
import FieldTip from '../ui/FieldTip';

const AmountAutoCalcNote = ({ type }) => {
  if (type === 'private_equity') return <p className="text-xs text-slate-500">Auto-calculates from Share Price x Shares, or enter directly</p>;
  if (type === 'etf') return <p className="text-xs text-slate-500">Auto-calculates from Units x Price per Unit, or enter directly</p>;
  if (type === 'gold') return <p className="text-xs text-slate-500">Auto-calculates from Weight x Price per Ounce, or enter directly</p>;
  return null;
};

const MiddleColumn = ({ formData, onFieldChange, config, setConfig }) => {
  const type = formData.type;

  if (type === 'quoted_stocks') {
    return (
      <div className="space-y-2">
        <Label htmlFor="stock_exchange" className="portfolio-input-label">Stock Exchange</Label>
        <SearchableSelect
          value={formData.stock_exchange}
          onValueChange={(value) => onFieldChange('stock_exchange', value)}
          options={EXCHANGE_OPTIONS}
          placeholder="Select stock exchange"
          searchPlaceholder="Search exchange..."
          emptyMessage="No exchange found."
          dataTestId="stock-exchange-select"
        />
      </div>
    );
  }

  if (['government_bond', 'corporate_bond'].includes(type)) {
    return (
      <div className="space-y-2">
        <Label htmlFor="issuer_name" className="portfolio-input-label">Name of Issuer</Label>
        <Input
          id="issuer_name"
          data-testid="issuer-name-input"
          placeholder="e.g. U.S. Treasury, Apple Inc."
          value={formData.issuer_name}
          onChange={(e) => onFieldChange('issuer_name', e.target.value)}
          className="portfolio-input"
        />
      </div>
    );
  }

  if (type === 'mutual_fund') {
    return (
      <div className="space-y-2">
        <Label htmlFor="current_value" className="portfolio-input-label">Current Value<FieldTip field="current_value" /></Label>
        <Input
          id="current_value"
          data-testid="current-value-input"
          type="number"
          step="0.01"
          placeholder="Leave empty if same as amount"
          value={formData.current_value}
          onChange={(e) => onFieldChange('current_value', e.target.value)}
          className="portfolio-input"
        />
      </div>
    );
  }

  if (['etf', 'gold'].includes(type)) {
    return (
      <div className="space-y-2">
        <Label htmlFor="current_value" className="portfolio-input-label">Current Value (Auto-calculated)</Label>
        <Input id="current_value" type="number" step="0.01" value={formData.current_value} className="portfolio-input bg-slate-50" disabled readOnly />
        <p className="text-xs text-slate-500">
          {type === 'etf' && 'Calculated from Units x Current NAV'}
          {type === 'gold' && 'Calculated from Weight x Current Price per Oz'}
        </p>
      </div>
    );
  }

  if (type === 'real_estate') {
    return (
      <div className="space-y-2">
        <Label htmlFor="current_value" className="portfolio-input-label">Current Value<FieldTip field="current_value_real_estate" /></Label>
        <Input
          id="current_value"
          data-testid="current-value-input"
          type="number"
          step="0.01"
          placeholder="Current market price of the property"
          value={formData.current_value}
          onChange={(e) => onFieldChange('current_value', e.target.value)}
          className="portfolio-input"
        />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="current_value" className="portfolio-input-label">Current Value<FieldTip field="current_value" /></Label>
      <Input
        id="current_value"
        data-testid="current-value-input"
        type="number"
        step="0.01"
        placeholder="Leave empty if same as amount"
        value={formData.current_value}
        onChange={(e) => onFieldChange('current_value', e.target.value)}
        className="portfolio-input"
      />
    </div>
  );
};

const InvestmentFormMiddle = ({ formData, onFieldChange, config, setConfig, errors = {}, onBlur }) => (
  <>
    {/* Amount, Middle Column, and Currency */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="space-y-2">
        <Label htmlFor="amount" className="portfolio-input-label">
          Amount Invested <span className="text-red-500">*</span>
        </Label>
        <Input
          id="amount"
          data-testid="investment-amount-input"
          type="number"
          step="0.01"
          placeholder="e.g. 10000"
          value={formData.amount}
          onChange={(e) => onFieldChange('amount', e.target.value)}
          onBlur={() => onBlur?.('amount')}
          className={`portfolio-input ${errors.amount ? 'border-red-400' : ''}`}
        />
        <AmountAutoCalcNote type={formData.type} />
        {errors.amount && <p className="text-xs text-red-500 mt-1">{errors.amount}</p>}
      </div>

      <MiddleColumn formData={formData} onFieldChange={onFieldChange} config={config} setConfig={setConfig} />

      <div className="space-y-2">
        <Label htmlFor="currency" className="portfolio-input-label">Currency <span className="text-red-500">*</span><FieldTip field="currency" /></Label>
        <SearchableSelect
          value={formData.currency}
          onValueChange={(value) => onFieldChange('currency', value)}
          options={CURRENCY_OPTIONS}
          placeholder="Select currency"
          searchPlaceholder="Search currency..."
          emptyMessage="No currency found."
          dataTestId="currency-select"
        />
        {errors.currency && <p className="text-xs text-red-500 mt-1">{errors.currency}</p>}
      </div>
    </div>

    {/* Current Value for quoted stocks and bonds (separate row) */}
    {(formData.type === 'quoted_stocks' || ['government_bond', 'corporate_bond'].includes(formData.type)) && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="current_value" className="portfolio-input-label">Current Value<FieldTip field="current_value" /></Label>
          <Input
            id="current_value"
            data-testid="current-value-input"
            type="number"
            step="0.01"
            placeholder="Leave empty if same as amount"
            value={formData.current_value}
            onChange={(e) => onFieldChange('current_value', e.target.value)}
            className="portfolio-input"
          />
        </div>
      </div>
    )}

    {/* Institution and Country */}
    {!['real_estate', 'etf', 'gold'].includes(formData.type) && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="institution" className="portfolio-input-label">Institution/Broker</Label>
          <Input
            id="institution"
            data-testid="institution-input"
            placeholder="e.g. Charles Schwab, Coinbase"
            value={formData.institution}
            onChange={(e) => onFieldChange('institution', e.target.value)}
            className="portfolio-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="country" className="portfolio-input-label">Country</Label>
          <SearchableSelect
            value={formData.country}
            onValueChange={(value) => onFieldChange('country', value)}
            options={COUNTRY_OPTIONS}
            placeholder="Select country"
            searchPlaceholder="Search country..."
            emptyMessage="No country found."
            dataTestId="country-select"
          />
        </div>
      </div>
    )}
  </>
);

export default InvestmentFormMiddle;
